(function (root) {
  'use strict';

  function parseMoney(text, locale, currency) {
    if (typeof text !== 'string') return null;
    let formatter;
    try { formatter = new Intl.NumberFormat(locale, { style: 'currency', currency }); } catch (_) { return null; }
    const parts = formatter.formatToParts(12345.67);
    const decimal = parts.find(part => part.type === 'decimal')?.value;
    const group = parts.find(part => part.type === 'group')?.value;
    const symbol = parts.find(part => part.type === 'currency')?.value;
    const digits = formatter.resolvedOptions().maximumFractionDigits;
    let value = text.trim().replace(/[\u00a0\u202f\u200e\u200f]/g, ' ');
    if (symbol) value = value.replace(symbol, '');
    value = value.replace(currency, '').trim();
    if (!/^[\d.,\s]+$/.test(value)) return null;
    value = value.replace(/\s/g, '');
    const sections = decimal ? value.split(decimal) : [value];
    if (sections.length > 2) return null;
    let integer = sections[0];
    const fraction = sections[1] || '';
    if (fraction && (fraction.length !== digits || !/^\d+$/.test(fraction))) return null;
    if (group && integer.includes(group)) {
      const groups = integer.split(group);
      if (!/^\d{1,3}$/.test(groups[0]) || groups.slice(1).some(part => !/^\d{3}$/.test(part))) return null;
      integer = groups.join('');
    }
    if (!/^\d+$/.test(integer)) return null;
    const amount = Number(integer) * (10 ** digits) + Number(fraction.padEnd(digits, '0'));
    return Number.isSafeInteger(amount) && amount > 0 ? amount : null;
  }

  function weeklyBreakdown(totalMinor, boxes, weeksPerBox) {
    if (!Number.isSafeInteger(totalMinor) || totalMinor <= 0 || !Number.isSafeInteger(boxes) || boxes < 1 || !Number.isFinite(weeksPerBox) || weeksPerBox <= 0) return null;
    const weeks = boxes * weeksPerBox;
    if (!Number.isSafeInteger(weeks) || weeks <= 0) return null;
    return { total: totalMinor, boxes, weeksPerBox, weeks, amount: Math.round(totalMinor / weeks) };
  }

  function weeklyAmount(totalMinor, boxes, weeksPerBox) {
    return weeklyBreakdown(totalMinor, boxes, weeksPerBox)?.amount ?? null;
  }

  function boxesForVariant(variant) {
    if (!variant || variant.requiresSellingPlan !== false || typeof variant.sku !== 'string') return null;
    // Verified one-box refill (1P) and kit (1S) SKUs. Multi-pair SKUs need their own evidence.
    return /^PRESS-(COSY|FLUFFY|WISPY|SASSY)-1[PS](?:-(8|10|12|14)MM)?$/.test(variant.sku) ? 1 : null;
  }

  function clustersForVariant(variant) {
    if (boxesForVariant(variant) === null) return null;
    return /^PRESS-(COSY|FLUFFY)-/.test(variant.sku) ? 36 : 30;
  }

  function bundleContents(slots, optionNames, variants) {
    if (!Array.isArray(slots) || !slots.length || !Array.isArray(optionNames) || !optionNames.length || !Array.isArray(variants)) return null;
    if (optionNames.some(name => typeof name !== 'string' || !name) || new Set(optionNames).size !== optionNames.length) return null;
    let boxes = 0;
    const clusterCounts = new Set();
    for (const slot of slots) {
      if (!Array.isArray(slot) || slot.length !== optionNames.length) return null;
      const values = new Map();
      for (const selection of slot) {
        if (!selection || selection.disabled || !optionNames.includes(selection.name) || typeof selection.value !== 'string' || !selection.value || values.has(selection.name)) return null;
        values.set(selection.name, selection.value);
      }
      const matches = variants.filter(variant => Array.isArray(variant.options) && variant.options.length === optionNames.length && variant.options.every((value, index) => value === values.get(optionNames[index])));
      if (matches.length !== 1) return null;
      const count = boxesForVariant(matches[0]);
      if (count === null) return null;
      boxes += count;
      clusterCounts.add(clustersForVariant(matches[0]));
      if (!Number.isSafeInteger(boxes)) return null;
    }
    return { boxes, clusterCounts: Array.from(clusterCounts).sort((a, b) => a - b) };
  }

  function bundleBoxCount(slots, optionNames, variants) {
    return bundleContents(slots, optionNames, variants)?.boxes ?? null;
  }

  function configuredDealQuantity(dealBlock, selectedBarId) {
    if (!dealBlock || dealBlock.showPricesPerItem !== false || !Array.isArray(dealBlock.dealBars) || !selectedBarId) return null;
    const matches = dealBlock.dealBars.filter(bar => bar.id === selectedBarId);
    if (matches.length !== 1) return null;
    const bar = matches[0];
    if (bar.sellingPlanEnabled || bar.sellingPlanGid || bar.quantitySelector || bar.featuredProductGID) return null;
    if (['freeGifts', 'multipleGiftsSelectors', 'upsells'].some(key => !Array.isArray(bar[key]) || bar[key].length)) return null;
    const positiveInteger = value => Number.isSafeInteger(value) && value > 0;
    if (bar.dealBarType === 'quantity-break') return positiveInteger(bar.quantity) ? bar.quantity : null;
    if (bar.dealBarType === 'bxgy' && positiveInteger(bar.buyQuantity) && positiveInteger(bar.getQuantity)) {
      const quantity = bar.buyQuantity + bar.getQuantity;
      return positiveInteger(quantity) ? quantity : null;
    }
    return null;
  }

  function singleVariantBundleContents(variantId, quantity, variants) {
    if (!variantId || !Number.isSafeInteger(quantity) || quantity < 1 || !Array.isArray(variants)) return null;
    const matches = variants.filter(variant => String(variant.id) === String(variantId));
    if (matches.length !== 1) return null;
    const count = boxesForVariant(matches[0]);
    const boxes = count === null ? null : count * quantity;
    return Number.isSafeInteger(boxes) && boxes > 0 ? { boxes, clusterCounts: [clustersForVariant(matches[0])] } : null;
  }

  function singleVariantBundleBoxes(variantId, quantity, variants) {
    return singleVariantBundleContents(variantId, quantity, variants)?.boxes ?? null;
  }

  function salonComparison(state, reference) {
    if (!state || !reference) return null;
    const { minRefillMinor, maxRefillMinor, refillWeeks, savingsWeeks } = reference;
    if (![state.total, state.weeks, minRefillMinor, maxRefillMinor, refillWeeks, savingsWeeks].every(value => Number.isSafeInteger(value) && value > 0) || maxRefillMinor < minRefillMinor) return null;
    // Compare the unrounded weekly costs. Floor savings so rounding cannot inflate the claim.
    const savingNumerator = (maxRefillMinor * state.weeks - state.total * refillWeeks) * savingsWeeks;
    const savingDenominator = refillWeeks * state.weeks;
    if (!Number.isSafeInteger(savingNumerator) || !Number.isSafeInteger(savingDenominator)) return null;
    return {
      low: Math.round(minRefillMinor / refillWeeks),
      high: Math.round(maxRefillMinor / refillWeeks),
      savings: Math.max(0, Math.floor(savingNumerator / savingDenominator))
    };
  }

  const api = { parseMoney, weeklyAmount, weeklyBreakdown, boxesForVariant, clustersForVariant, bundleContents, bundleBoxCount, configuredDealQuantity, singleVariantBundleContents, singleVariantBundleBoxes, salonComparison };
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (!root.document) return;
  if (root.__ig66Loaded) return;
  root.__ig66Loaded = true;
  const document = root.document;
  const configElement = document.getElementById('ig-id66-config');
  const widget = document.getElementById('ig-id66');
  if (!configElement || !widget) return;
  let config;
  try { config = JSON.parse(configElement.textContent); } catch (_) { return; }
  if (typeof config.description !== 'string' || !config.description.includes('__CLUSTERS__') || typeof config.descriptionMixed !== 'string' || !config.descriptionMixed) return;
  const trigger = widget.querySelector('.ig-id66__trigger');
  const dialog = widget.querySelector('dialog');
  const line = widget.querySelector('[data-ig66-line]');
  const title = widget.querySelector('[data-ig66-title]');
  const description = widget.querySelector('.ig-id66__description');
  const weekly = widget.querySelector('[data-ig66-weekly]');
  const salonPrice = widget.querySelector('[data-ig66-salon-price]');
  const savingsRow = widget.querySelector('[data-ig66-savings-row]');
  const savings = widget.querySelector('[data-ig66-savings]');
  const boundBlocks = new WeakSet();
  const formatter = new Intl.NumberFormat(config.locale, { style: 'currency', currency: config.currency });
  const wholeFormatter = new Intl.NumberFormat(config.locale, { style: 'currency', currency: config.currency, maximumFractionDigits: 0 });
  const divisor = 10 ** formatter.resolvedOptions().maximumFractionDigits;
  let active = false;
  let scheduled = false;
  let host;
  let previousOverflow = null;
  let previousFocus = null;
  let observer;

  function track(name) {
    const detail = { experiment: '66', variant: 'v1', product_id: config.productId, event: name };
    document.dispatchEvent(new CustomEvent('mayson:experiment-event', { detail }));
    if (Array.isArray(root.dataLayer)) root.dataLayer.push(detail);
    root.igEvents = root.igEvents || [];
    root.igEvents.push({ event: name, properties: detail });
  }

  function positionDialog() {
    if (!dialog.open) return;
    const rect = trigger.getBoundingClientRect();
    const left = Math.max(16, Math.min(rect.left, root.innerWidth - dialog.offsetWidth - 16));
    const top = Math.max(16, Math.min(rect.bottom + 10, root.innerHeight - dialog.offsetHeight - 16));
    dialog.style.left = `${left}px`;
    dialog.style.top = `${top}px`;
    const info = trigger.querySelector('.ig-id66__info').getBoundingClientRect();
    const pointer = Math.max(16, Math.min(info.left + info.width / 2 - left, dialog.offsetWidth - 16));
    dialog.style.setProperty('--ig66-pointer-left', `${pointer}px`);
    dialog.toggleAttribute('data-pointer-hidden', top < rect.bottom);
  }

  function closeDialog() {
    if (dialog.open) dialog.close();
  }

  trigger.addEventListener('click', function () {
    if (!active || widget.hidden || dialog.open) return;
    previousFocus = document.activeElement;
    previousOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    dialog.showModal();
    trigger.setAttribute('aria-expanded', 'true');
    positionDialog();
    track('price_per_week_open');
  });
  widget.querySelector('.ig-id66__close').addEventListener('click', closeDialog);
  dialog.addEventListener('keydown', function (event) {
    if (event.key !== 'Tab') return;
    const focusable = Array.from(dialog.querySelectorAll('button, a[href], input, select, textarea, [tabindex="0"]')).filter(element => !element.disabled && element.getClientRects().length);
    const first = focusable[0], last = focusable[focusable.length - 1];
    if (first && event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (last && !event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  });
  dialog.addEventListener('click', function (event) {
    if (event.target !== dialog) return;
    const rect = dialog.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) closeDialog();
  });
  dialog.addEventListener('close', function () {
    trigger.setAttribute('aria-expanded', 'false');
    if (previousOverflow !== null) document.documentElement.style.overflow = previousOverflow;
    previousOverflow = null;
    if (previousFocus?.isConnected && !widget.hidden) previousFocus.focus({ preventScroll: true });
    track('price_per_week_close');
  });
  root.addEventListener('resize', function () { positionDialog(); schedule(); });

  function schedule() {
    if (!active || scheduled) return;
    scheduled = true;
    root.requestAnimationFrame(function () { scheduled = false; refresh(); });
  }

  function bindBlock(block) {
    if (boundBlocks.has(block)) return;
    boundBlocks.add(block);
    ['variants-changed', 'variant-selected', 'deal-bar-selected'].forEach(name => block.addEventListener(name, schedule));
  }

  function selectedPrice() {
    if (!host) return null;
    const section = host.closest('.shopify-section') || host;
    const sellingPlan = Array.from(section.querySelectorAll('[name="selling_plan"]')).find(input => input.value && (!['radio', 'checkbox'].includes(input.type) || input.checked));
    if (sellingPlan) return null;
    const selected = host.querySelectorAll('.kaching-bundles__bar--selected');
    if (selected.length !== 1) return null;
    const bar = selected[0];
    const block = bar.closest('kaching-bundles-block');
    let dealBlock, blockProduct;
    try {
      dealBlock = JSON.parse(block?.getAttribute('deal-block'));
      blockProduct = JSON.parse(block?.getAttribute('product'));
    } catch (_) { return null; }
    if (!blockProduct || String(blockProduct.id) !== String(config.productId)) return null;
    const quantity = configuredDealQuantity(dealBlock, bar.getAttribute('data-deal-bar-id'));
    if (quantity === null) return null;
    const prices = bar.querySelectorAll('.kaching-bundles__bar-price');
    const slots = bar.querySelectorAll('.kaching-bundles__bar-variant-selector-wrapper');
    if (prices.length !== 1) return null;
    let contents;
    if (slots.length) {
      if (slots.length !== quantity) return null;
      const selections = Array.from(slots, slot => Array.from(slot.querySelectorAll('select.kaching-bundles__bar-variant-select'), select => ({
        name: select.getAttribute('data-name'),
        value: select.value,
        disabled: !select.selectedOptions[0] || select.selectedOptions[0].disabled
      })));
      contents = bundleContents(selections, config.optionNames, config.variants);
    } else {
      if (dealBlock.differentVariantsEnabled !== false) return null;
      // These bars repeat the native selected product; read its current form value, not the initial URL/config.
      const ids = new Set(Array.from(section.querySelectorAll('product-form form[action*="/cart/add"] [name="id"]'), input => input.value).filter(Boolean));
      if (ids.size !== 1) return null;
      contents = singleVariantBundleContents(Array.from(ids)[0], quantity, config.variants);
    }
    if (!contents) return null;
    const total = parseMoney(prices[0].textContent, config.locale, config.currency);
    const state = weeklyBreakdown(total, contents.boxes, config.weeksPerBox);
    return state ? { ...state, clusterCounts: contents.clusterCounts } : null;
  }

  function setText(element, value) {
    if (element.textContent !== value) element.textContent = value;
  }

  function hide() {
    widget.hidden = true;
    closeDialog();
  }

  function refresh() {
    if (!active) { hide(); return; }
    host = document.querySelector('product-information:not([data-quick-view="true"])');
    if (!host) { hide(); return; }
    host.querySelectorAll('kaching-bundles-block').forEach(bindBlock);
    // The theme moves its price block outside product-information on mobile.
    const section = host.closest('.shopify-section') || host;
    const anchor = section.querySelector('.price-test-container')?.parentElement;
    const state = selectedPrice();
    if (!anchor || !state) { hide(); return; }
    if (!widget.isConnected) closeDialog();
    if (widget.previousElementSibling !== anchor) anchor.insertAdjacentElement('afterend', widget);
    const amount = formatter.format(state.amount / divisor);
    if (line.dataset.amount !== amount) {
      const pieces = config.line.split('__AMOUNT__');
      const strong = document.createElement('strong');
      strong.textContent = amount;
      line.replaceChildren(document.createTextNode(pieces[0]), strong, document.createTextNode(pieces[1] || ''));
      line.dataset.amount = amount;
    }
    setText(title, config.title.replace('__AMOUNT__', amount));
    setText(description, state.clusterCounts.length === 1
      ? config.description.replace('__CLUSTERS__', String(state.clusterCounts[0]))
      : config.descriptionMixed);
    setText(weekly, config.weekly.replace('__AMOUNT__', amount));
    const comparison = config.currency === 'EUR' ? salonComparison(state, config.salonComparison) : null;
    if (comparison && salonPrice && savingsRow && savings) {
      setText(salonPrice, config.salonPrice.replace('__LOW__', formatter.format(comparison.low / divisor)).replace('__HIGH__', formatter.format(comparison.high / divisor)));
      const wholeSaving = Math.floor(comparison.savings / divisor);
      savingsRow.hidden = wholeSaving <= 0;
      setText(savings, config.savings.replace('__AMOUNT__', wholeFormatter.format(wholeSaving)));
    }
    widget.dataset.boxes = String(state.boxes);
    widget.dataset.totalMinor = String(state.total);
    widget.dataset.weeks = String(state.weeks);
    widget.hidden = false;
    positionDialog();
  }

  root.igId66ApplyVariant = function (cell) {
    root.__ig66Variant = cell === 'v1' ? 'v1' : 'control';
    active = root.__ig66Variant === 'v1';
    if (!active) {
      if (observer) observer.disconnect();
      hide();
      return;
    }
    host = document.querySelector('product-information:not([data-quick-view="true"])');
    if (!host) return;
    if (!observer) observer = new MutationObserver(function (records) {
      if (records.some(record => !widget.contains(record.target))) schedule();
    });
    observer.disconnect();
    observer.observe(host.closest('.shopify-section') || host, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['class', 'value', 'data-value', 'disabled', 'checked', 'deal-block', 'product'] });
    refresh();
  };
  document.addEventListener('change', function (event) {
    if ((host?.closest('.shopify-section') || host)?.contains(event.target)) schedule();
  });
  document.addEventListener('shopify:section:load', function () { if (active) root.igId66ApplyVariant('v1'); });
  const override = config.preview ? new URLSearchParams(root.location.search).get('cdxtest') : null;
  root.igId66ApplyVariant(override === '66-v1' ? 'v1' : override === '66-control' ? 'control' : root.__ig66Variant);
})(typeof window === 'object' ? window : globalThis);
